<template>
  <div
      :class="isModal ? 'w-full' : 'mx-auto max-w-md rounded-2xl bg-white p-8 shadow-xl dark:bg-gray-800'"
  >
    <component
        :is="isModal ? 'h2' : 'h1'"
        :class="isModal
          ? 'mb-4 text-start text-lg font-bold tracking-tight text-gray-900 dark:text-gray-50'
          : 'mb-6 text-center text-3xl font-extrabold text-gray-900 dark:text-gray-100'"
    >{{ $t('auth.login_title') }}</component>

    <div
        v-if="showExistingAccountNotice"
        class="mb-5 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm leading-relaxed text-indigo-900 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-200"
        role="status"
        aria-live="polite"
    >
      {{ $t('auth.existing_account_meal_plan_notice') }}
    </div>

    <form :class="isModal ? 'space-y-4' : 'space-y-6'" @submit.prevent="onSubmit">

      <div class="relative">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="email">{{
            $t('auth.email')
          }}</label>
        <input
            id="email"
            v-model="form.email"
            :placeholder="$t('auth.email')"
            :class="inputClass"
            required
            type="email"
        />
      </div>

      <div class="relative">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300" for="password">{{
            $t('auth.password')
          }}</label>
        <div class="relative mt-1">
          <input
              id="password"
              v-model="form.password"
              :placeholder="$t('auth.password_placeholder')"
              :type="showPassword ? 'text' : 'password'"
              :class="inputClass"
              required
          />
          <button
              class="absolute inset-y-0 right-3 flex items-center cursor-pointer"
              tabindex="-1"
              type="button"
              @click="togglePassword"
          >
            <component :is="showPassword ? EyeOff : Eye" class="h-5 w-5 text-gray-500"/>
          </button>
        </div>
      </div>

      <div class="flex items-center justify-between">
        <label class="inline-flex items-center">
          <input
              v-model="form.remember"
              class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:focus:ring-indigo-600"
              type="checkbox"
          />
          <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">{{ $t('auth.remember_me') }}</span>
        </label>
        <button
            class="cursor-pointer text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
            type="button"
            @click="goToForgot"
        >
          {{ $t('auth.forgot_password') }}
        </button>
      </div>

      <div v-if="error" class="rounded-lg bg-red-50 px-3 py-2 text-center text-sm text-red-600 dark:bg-red-950/40 dark:text-red-400">
        {{ error }}
      </div>

      <button
          :aria-busy="loading"
          :disabled="loading"
          :class="[
            'flex w-full cursor-pointer items-center justify-center gap-2 font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50',
            isModal
              ? 'rounded-xl bg-linear-to-r from-indigo-600 to-violet-600 py-3.5 text-[15px] shadow-lg shadow-indigo-500/25 hover:from-indigo-500 hover:to-violet-500'
              : 'rounded-lg bg-linear-to-r from-blue-500 to-indigo-600 py-3 hover:from-blue-600 hover:to-indigo-700',
          ]"
          type="submit"
      >
        <Loader v-if="loading" class="h-5 w-5 shrink-0 animate-spin"/>
        <span>{{ loading ? $t('auth.signing_in') : $t('auth.login_button') }}</span>
      </button>

      <div class="relative my-4 flex items-center justify-center">
        <div class="absolute inset-0 flex items-center">
          <div class="w-full border-t border-gray-200 dark:border-gray-600"></div>
        </div>
        <div
            :class="isModal
              ? 'relative bg-white/95 px-3 text-sm text-gray-500 dark:bg-gray-900/95 dark:text-gray-400'
              : 'relative bg-white px-4 text-sm text-gray-500 dark:bg-gray-800 dark:text-gray-400'"
        >
          {{ $t('auth.or') }}
          <span class="font-semibold text-indigo-600 dark:text-indigo-400">{{ $t('auth.recommended') }}</span>
        </div>
      </div>

      <AppleLoginButton v-if="isIos" class="mb-3" />
      <GoogleLoginButton/>
    </form>

    <p class="mt-5 text-center text-sm text-gray-600 dark:text-gray-400">
      {{ $t('auth.dont_have_account') }}
      <router-link
          v-if="!isModal"
          class="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
          to="/register">
        {{ $t('auth.signup') }}
      </router-link>
      <a
          v-else
          class="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
          @click="$emit('switch-tab', 'register');">
        {{ $t('auth.signup') }}
      </a>
    </p>
  </div>
</template>

<script setup>
import { computed, reactive, ref, defineProps, defineEmits, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { Eye, EyeOff, Loader } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import GoogleLoginButton from '@/components/auth/GoogleLoginButton.vue'
import AppleLoginButton from '@/components/auth/AppleLoginButton.vue'
import { getApiErrorMessage } from '@/utils/error-handler'
import { useConsentStore } from '@/stores/consent'

const props = defineProps({
  isModal: { type: Boolean, default: false }
})

const inputClass = computed(() => [
  'ltr mt-1 block w-full border px-3.5 py-3 text-left text-gray-900 placeholder-gray-400 transition focus:outline-none focus:ring-2 dark:text-gray-100',
  props.isModal
    ? 'rounded-xl border-gray-200 bg-white/90 focus:border-indigo-500 focus:ring-indigo-500/30 dark:border-gray-600 dark:bg-gray-800/60 dark:focus:border-indigo-400 dark:focus:ring-indigo-400/25'
    : 'rounded-lg border-gray-300 bg-gray-50 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700',
])

const isIos = window.Capacitor && window.Capacitor.getPlatform() === 'ios'

const emit = defineEmits(['success', 'switch-tab', 'close-modal'])

const { t } = useI18n()
const form = reactive({ email: '', password: '', remember: false })
const error = ref('')
const loading = ref(false)
const showPassword = ref(false)

const auth = useAuthStore()
const consentStore = useConsentStore()
const router = useRouter()
const route = useRoute()
const showExistingAccountNotice = computed(() => route.query.notice === 'existing_account')

function applyEmailFromRouteQuery () {
  const q = route.query.email
  if (typeof q !== 'string' || !q.trim()) {
    return
  }
  if (form.email) {
    return
  }
  form.email = q.trim().toLowerCase()
}

onMounted(applyEmailFromRouteQuery)
watch(() => route.query.email, applyEmailFromRouteQuery)

function togglePassword () {
  showPassword.value = !showPassword.value
}

async function onSubmit () {
  error.value = ''
  loading.value = true
  try {
    // Check for redirect in query params or stored in auth store
    const storedUrl = auth.getOriginalUrl()
    const redirectTo = route.query.redirect || storedUrl || '/'

    await auth.login({
      email: form.email, password: form.password, remember: form.remember
    })

    await consentStore.trackLogin({ method: 'email' })

    if (props.isModal) {
      emit('success')
    } else {
      router.push(redirectTo)
    }
  } catch (e) {
    error.value = getApiErrorMessage(e, t, 'auth.login_error')
  } finally {
    loading.value = false
  }
}

function goToForgot () {
  if (props.isModal) {
    emit('switch-tab', 'forgot-password')
  } else {
    router.push('/forgot-password')
  }
}
</script>
